import React from 'react';
import {Alert, Box, Button, Typography} from '@mui/material';
import {showReportDialog} from '@sentry/react';
import {themeColors} from '../theme/Theme';
import SaltusThemeProvider from '../contexts/ThemeProvider';

const classes = {
  overlay: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    position: 'absolute',
  },
  root: {
    color: '#FFFFFF',
    background: themeColors.backgroundDark,
    padding: '20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  code: {
    display: 'inline-block',
    borderRight: '3px solid rgba(255, 255, 255, .3)',
    margin: '0',
    marginRight: '20px',
    padding: '10px 23px 10px 0',
    fontSize: '20px',
    verticalAlign: 'top',
  },
  headline: {
    fontSize: '14px',
    fontWeight: 'normal',
    lineHeight: 'inherit',
    margin: 0,
    padding: 0,
  },
  linkWrapper: {
    marginTop: '0.1rem',
    display: 'flex',
    justifyContent: 'space-around',
  },
  button: {
    backgroundColor: themeColors.coral,
    marginRight: '1rem',
  },
};

interface ErrorProps {
  code?: string;
  text: string;
  retry?: () => void;
  refresh?: boolean;
  error?: Error;
  onShowDialog?: () => void;
  eventId?: string;
  inline?: boolean;
}

const ErrorMessage: React.FC<{error: Error | undefined}> = ({error}) => {
  const errorMessageNeedsLink = error?.message?.includes('oneview error id');
  return error ? (
    <Box p={5}>
      <Typography variant="h5">
        {error.message}
        {errorMessageNeedsLink ? (
          <>
            by{' '}
            <strong>
              <a
                href="https://saltus.freshdesk.com/support/tickets/new"
                target="_blank"
                rel="noreferrer"
              >
                Clicking here
              </a>
            </strong>
          </>
        ) : null}
      </Typography>
    </Box>
  ) : null;
};

export const ErrorPage = (props: ErrorProps) => {
  const {code, text, retry, refresh, error, onShowDialog, eventId, inline} = props;

  if (inline) {
    return (
      <Alert severity="error">
        {code ? <Typography variant="h6">{code}</Typography> : null}
        <Typography variant="body1">{text}</Typography>
        <ErrorMessage error={error} />
      </Alert>
    );
  }

  return (
    <SaltusThemeProvider>
      <Box data-test="Error message display" sx={{...classes.root, ...classes.overlay}}>
        <img
          style={{
            width: '100px',
            height: '25px',
            objectFit: 'contain',
          }}
          alt="company logo"
          src="/logo.svg"
        />
        <Box mt={2}>
          <div
            style={{
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'row',
              textAlign: 'center',
              lineHeight: '49px',
              alignItems: 'center',
            }}
          >
            {code ? (
              <Typography variant="h3" sx={classes.code}>
                {code}
              </Typography>
            ) : null}
            <Box pl={2}>
              <Typography variant="h2" sx={classes.headline}>
                {text}
              </Typography>
            </Box>
          </div>
          <ErrorMessage error={error} />
          <Box sx={classes.linkWrapper}>
            <Button
              sx={classes.button}
              variant="contained"
              color="secondary"
              onClick={() => {
                window.history.back();
              }}
            >
              Go back
            </Button>
            <Button sx={classes.button} variant="contained" color="secondary" href="/">
              Go to home
            </Button>
            {refresh ? (
              <Button
                sx={classes.button}
                variant="contained"
                color="secondary"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Refresh page
              </Button>
            ) : null}
            <Button
              sx={classes.button}
              variant="contained"
              color="secondary"
              onClick={() => {
                showReportDialog({eventId});
                onShowDialog?.();
              }}
            >
              Provide feedback
            </Button>
            {retry ? (
              <Button color="secondary" sx={classes.button} onClick={() => retry()}>
                Retry
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </SaltusThemeProvider>
  );
};
